import React, { useState, useEffect, useMemo } from "react";
import { Col, Row } from "reactstrap";
import Flatpickr from "react-flatpickr";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { getDashboardKpis } from "../../slices/configuracion/dashboard/thunk";
import { getFarmacia } from "../../slices/configuracion/farmacias/thunk";
import { clearCache } from "../../slices/configuracion/dashboard/reducer";
import { DatosSesion } from "../../Components/Common/DatosSesion";
import { getProveedoresDashboard } from "../../helpers/backend_helper";
import ModalDocumentacionMetricas from "./ModalDocumentacionMetricas";

// Formatear fechas a YYYY-MM-DD
const formatearFecha = (fecha) => {
  if (!fecha) return "";
  return fecha instanceof Date
    ? fecha.toISOString().split("T")[0]
    : new Date(fecha).toISOString().split("T")[0];
};

// Formatear fechas para mostrar
const formatearFechaParaMostrar = (fecha) => {
  if (!fecha) return "";
  const date = new Date(fecha);
  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Obtener fecha actual y hace 30 días
const obtenerFechaActual = () => {
  const hoy = new Date();
  return formatearFecha(hoy);
};

const obtenerFechaHace30Dias = () => {
  const hoy = new Date();
  const hace30 = new Date(hoy);
  hace30.setDate(hace30.getDate() - 30);
  return formatearFecha(hace30);
};

// Función para obtener saludo según la hora
const obtenerSaludo = () => {
  const hora = new Date().getHours();

  if (hora >= 5 && hora < 12) {
    return "¡Buenos días";
  } else if (hora >= 12 && hora < 19) {
    return "¡Buenas tardes";
  } else {
    return "¡Buenas noches";
  }
};

// Crear selectores memoizados fuera del componente
const selectDashboardState = (state) => state.Dashboard;
const selectFarmaciasState = (state) => state.Farmacias;

const dashboardSelector = createSelector(
  [selectDashboardState, selectFarmaciasState],
  (dashboardState, farmaciasState) => ({
    error: dashboardState.error,
    farmacia: farmaciasState?.farmacia || null,
  })
);

const Section = (props) => {
  const dispatch = useDispatch();

  // Estado para modal de documentación
  const [modalDocumentacion, setModalDocumentacion] = useState(false);

  // Estado para las fechas - inicializar con últimos 30 días
  const [fechaInicio, setFechaInicio] = useState(obtenerFechaHace30Dias());
  const [fechaFin, setFechaFin] = useState(obtenerFechaActual());
  const [codigoProveedor, setCodigoProveedor] = useState(null);
  const [aseguradoraSeleccionada, setAseguradoraSeleccionada] = useState("MyH+");
  const [cargandoFarmacia, setCargandoFarmacia] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [cargandoProveedores, setCargandoProveedores] = useState(false);
  const [exportando, setExportando] = useState(false);

  // Opciones de aseguradora
  const opcionesAseguradora = [
    { value: "MyH+", label: "MyH+" },
    { value: "Seguros Mercantil", label: "Seguros Mercantil" },
    { value: "LATINA SALUD", label: "Latina Salud" },
    { value: "HISPANA DE SEGUROS", label: "Hispana de Seguros" },
    { value: "REAL SEGUROS", label: "Real Seguros" },
    { value: "Todas", label: "Todas las aseguradoras" }
  ];

  // Obtener el ID de la farmacia del usuario
  const farmaciaId = props.farmaciaId;
  const perfil = DatosSesion("perfil") || "";
  const esAdministrador = (perfil === "Administrador" || perfil === "MyHealth");
  const esMyHealth = perfil === "MyHealth";
  const usuarioId = DatosSesion("id") || null;

  // Obtener datos de Redux usando el selector memoizado
  const { error, farmacia } = useSelector(dashboardSelector);

  // Determinar si los datos provienen de la caché
  const datosDesdeCache = error === "Datos obtenidos de caché";

  // Limpiar caché al montar el componente o cuando cambia el usuario
  useEffect(() => {
    const ultimoUsuarioId = localStorage.getItem('ultimo_usuario_dashboard');
    
    if (ultimoUsuarioId !== usuarioId) {
      dispatch(clearCache());
      localStorage.setItem('ultimo_usuario_dashboard', usuarioId);
    }
  }, [dispatch, usuarioId]);

  // Efecto para cargar los datos de la farmacia si el usuario tiene una asignada
  useEffect(() => {
    if (farmaciaId) {
      setCargandoFarmacia(true);
      dispatch(getFarmacia(farmaciaId));
    }
  }, [dispatch, farmaciaId]);

  // Efecto para actualizar el código de proveedor cuando se carga la farmacia
  // Solo para usuarios NO administradores
  useEffect(() => {
    if (!esAdministrador) {
      if (farmacia && farmacia.codigo_myh && farmacia.codigo_myh.trim() !== "") {
        const codigo = farmacia.codigo_myh;
        setCodigoProveedor(codigo);
        setCargandoFarmacia(false);
        
        // Notificar al componente padre sobre el código del proveedor
        if (props.onProveedorChange) {
          props.onProveedorChange(codigo);
        }
      } else {
        // Si no hay código de proveedor válido, establecerlo como null
        setCodigoProveedor(null);
        setCargandoFarmacia(false);
        
        // Notificar al componente padre
        if (props.onProveedorChange) {
          props.onProveedorChange(null);
        }
      }
    } else {
      // Para administradores, iniciar sin filtro
      setCargandoFarmacia(false);
    }
  }, [farmacia, esAdministrador]);

  // Efecto para cargar proveedores si es administrador
  useEffect(() => {
    if (esAdministrador) {
      setCargandoProveedores(true);
      getProveedoresDashboard()
        .then((response) => {
          if (response.data?.data) {
            setProveedores(response.data.data);
          }
          setCargandoProveedores(false);
        })
        .catch((err) => {
          console.error("Error al cargar proveedores:", err);
          setCargandoProveedores(false);
        });
    }
  }, [esAdministrador]);

  // Sincronizar estado interno cuando cambie el prop proveedorSeleccionado desde el padre
  useEffect(() => {
    if (props.proveedorSeleccionado !== undefined && props.proveedorSeleccionado !== codigoProveedor) {
      setCodigoProveedor(props.proveedorSeleccionado);
    }
  }, [props.proveedorSeleccionado]);

  // Sincronizar estado interno cuando cambie el prop aseguradoraSeleccionada desde el padre
  useEffect(() => {
    if (props.aseguradoraSeleccionada !== undefined && props.aseguradoraSeleccionada !== aseguradoraSeleccionada) {
      setAseguradoraSeleccionada(props.aseguradoraSeleccionada);
    }
  }, [props.aseguradoraSeleccionada]);

  // Manejar cambio de proveedor
  const handleProveedorChange = (e) => {
    const nuevoProveedor = e.target.value === "" ? null : e.target.value;
    setCodigoProveedor(nuevoProveedor);

    // Limpiar caché antes de cambiar de proveedor
    dispatch(clearCache());

    const params = {
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
    };

    // Solo agregar codigo_proveedor si existe y no está vacío
    if (nuevoProveedor && nuevoProveedor.trim() !== "") {
      params.codigo_proveedor = nuevoProveedor;
    }

    // Agregar aseguradora
    if (aseguradoraSeleccionada) {
      params.aseguradora = aseguradoraSeleccionada;
    }

    dispatch(getDashboardKpis(params));
    
    // Notificar al componente padre
    if (props.onProveedorChange) {
      props.onProveedorChange(nuevoProveedor);
    }
  };

  // Manejar cambio de aseguradora
  const handleAseguradoraChange = (e) => {
    const nuevaAseguradora = e.target.value;
    setAseguradoraSeleccionada(nuevaAseguradora);

    // Limpiar caché antes de cambiar de aseguradora
    dispatch(clearCache());

    const params = {
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
    };

    // Solo agregar codigo_proveedor si existe y no está vacío
    if (codigoProveedor && codigoProveedor.trim() !== "") {
      params.codigo_proveedor = codigoProveedor;
    }

    // Agregar aseguradora
    if (nuevaAseguradora) {
      params.aseguradora = nuevaAseguradora;
    }

    dispatch(getDashboardKpis(params));
    
    // Notificar al componente padre
    if (props.onAseguradoraChange) {
      props.onAseguradoraChange(nuevaAseguradora);
    }
  };

  // Manejar cambio de fechas
  const handleFechasChange = (selectedDates) => {
    if (selectedDates.length === 2) {
      const inicio = formatearFecha(selectedDates[0]);
      const fin = formatearFecha(selectedDates[1]);

      setFechaInicio(inicio);
      setFechaFin(fin);

      // Actualizar KPIs con las nuevas fechas
      const params = {
        fecha_inicio: inicio,
        fecha_fin: fin,
      };

      // Solo añadir código de proveedor si existe y no está vacío
      if (codigoProveedor && codigoProveedor.trim() !== "") {
        params.codigo_proveedor = codigoProveedor;
      }

      // Agregar aseguradora
      if (aseguradoraSeleccionada) {
        params.aseguradora = aseguradoraSeleccionada;
      }

      dispatch(getDashboardKpis(params));
      
      // Notificar al componente padre
      if (props.onFechasChange) {
        props.onFechasChange(inicio, fin);
      }
    }
  };

  // Manejar exportación
  const handleExport = async (formato) => {
    setExportando(true);
    try {
      const params = new URLSearchParams({
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
      });

      if (codigoProveedor && codigoProveedor.trim() !== "") {
        params.append("codigo_proveedor", codigoProveedor);
      }

      if (aseguradoraSeleccionada) {
        params.append("aseguradora", aseguradoraSeleccionada);
      }

      const url = `${process.env.REACT_APP_API_URL}/api/v1/dashboard/export/${formato}?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${DatosSesion("access_token")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Error al exportar: ${response.statusText}`);
      }

      // Descargar archivo
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      
      const extension = formato === "excel" ? "xlsx" : "pdf";
      const fechaStr = fechaInicio.replace(/-/g, "");
      const proveedorStr = codigoProveedor ? `_${codigoProveedor}` : "";
      link.download = `dashboard_farme_${fechaStr}${proveedorStr}.${extension}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error al exportar:", error);
      alert("Error al exportar el dashboard. Por favor, intente nuevamente.");
    } finally {
      setExportando(false);
    }
  };

  // Notificar fechas iniciales al componente padre inmediatamente
  useEffect(() => {
    if (props.onFechasChange) {
      props.onFechasChange(fechaInicio, fechaFin);
    }
  }, []); // Solo se ejecuta una vez al montar

  // Cargar KPIs iniciales
  useEffect(() => {
    // No cargar KPIs si estamos esperando datos de la farmacia
    if (farmaciaId && cargandoFarmacia) {
      return;
    }

    // Si es proveedor y aún no tiene código, esperar a que se cargue
    if (!esAdministrador && farmaciaId && !codigoProveedor) {
      return;
    }

    const params = {
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
    };

    // Solo añadir código de proveedor si existe y no está vacío
    if (codigoProveedor && codigoProveedor.trim() !== "") {
      params.codigo_proveedor = codigoProveedor;
    }

    // Agregar aseguradora
    if (aseguradoraSeleccionada) {
      params.aseguradora = aseguradoraSeleccionada;
    }

    // Llamar a la API (usará caché si está disponible)
    dispatch(getDashboardKpis(params));
  }, [dispatch, codigoProveedor, aseguradoraSeleccionada, cargandoFarmacia, farmaciaId, fechaInicio, fechaFin, esAdministrador]);

  return (
    <React.Fragment>
      <Row className="mb-3 pb-1">
        <Col xs={12}>
          <div className="d-flex align-items-lg-center flex-lg-row flex-column">
            {/* Mostrar saludo solo si no es soloFiltros */}
            {!props.soloFiltros && (
              <div className="flex-grow-1">
                <h4 className="fs-16 mb-1">
                  {obtenerSaludo()}, {props.nombre}!
                </h4>
                <p className="text-muted mb-0">
                  Aquí tienes un resumen de los KPIs de los últimos 30 días.
                  {farmaciaId && farmacia && (
                    <span>
                      {" "}
                      Mostrando datos para: <strong>{farmacia.nombre}</strong>
                    </span>
                  )}
                </p>
              </div>
            )}
            {/* Mostrar filtros solo si mostrarFiltros no es false */}
            {props.mostrarFiltros !== false && (
              <div className={props.soloFiltros ? "w-100" : "mt-3 mt-lg-0"}>
              <form action="#">
                <Row className="g-3 mb-0 align-items-center">
                  {esAdministrador && !props.soloFechas && (
                    <div className="col-sm-auto">
                      <div className="input-group">
                        <div className="input-group-text bg-success border-success text-white">
                          <i className="ri-store-2-line"></i>
                        </div>
                        <select
                          className="form-select shadow"
                          value={codigoProveedor || ""}
                          onChange={handleProveedorChange}
                          disabled={cargandoProveedores}
                          style={{ minWidth: '200px' }}
                        >
                          <option value="">Todos los proveedores</option>
                          {proveedores.map((prov) => (
                            <option key={prov.codigo} value={prov.codigo}>
                              {prov.nombre}
                            </option>
                          ))}
                        </select>
                      </div>
                      <small className="text-muted d-block mt-1">
                        <i className="ri-filter-3-line me-1"></i>
                        <strong>Filtrar por proveedor</strong>
                      </small>
                    </div>
                  )}
                  {/* Selector de Aseguradora - visible para todos (Admin y Farmacia) */}
                  {!props.soloProveedor && (
                    <div className="col-sm-auto">
                      <div className="input-group">
                        <div className="input-group-text bg-info border-info text-white">
                          <i className="ri-shield-check-line"></i>
                        </div>
                        <select
                          className="form-select shadow"
                          value={esMyHealth ? "MyH+" : aseguradoraSeleccionada}
                          onChange={handleAseguradoraChange}
                          disabled={esMyHealth}
                          style={{ minWidth: '200px' }}
                        >
                          {(esMyHealth
                            ? opcionesAseguradora.filter((opcion) => opcion.value === "MyH+")
                            : opcionesAseguradora
                          ).map((opcion) => (
                            <option key={opcion.value} value={opcion.value}>
                              {opcion.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <small className="text-muted d-block mt-1">
                        <i className="ri-filter-3-line me-1"></i>
                        <strong>Filtrar por aseguradora</strong>
                      </small>
                    </div>
                  )}
                  {/* Mostrar selector de fechas solo si NO es soloProveedor */}
                  {!props.soloProveedor && (
                  <div className="col-sm-auto">
                    <div className="card border-primary shadow-sm mb-0">
                      <div className="card-body p-3">
                        <div className="d-flex align-items-center gap-2">
                          <div className="flex-shrink-0">
                            <div className="avatar-xs">
                              <div className="avatar-title bg-primary-subtle text-primary rounded">
                                <i className="ri-calendar-2-line fs-16"></i>
                              </div>
                            </div>
                          </div>
                          <div className="flex-grow-1">
                            <div className="input-group">
                              <div className="input-group-text bg-primary border-primary text-white">
                                <i className="ri-calendar-2-line"></i>
                              </div>
                              <Flatpickr
                                className="form-control dash-filter-picker shadow"
                        options={{
                          mode: "range",
                          dateFormat: "d M, Y",
                          defaultDate: [
                            new Date(fechaInicio),
                            new Date(fechaFin),
                          ],
                          locale: {
                            firstDayOfWeek: 1,
                            weekdays: {
                              shorthand: [
                                "Do",
                                "Lu",
                                "Ma",
                                "Mi",
                                "Ju",
                                "Vi",
                                "Sa",
                              ],
                              longhand: [
                                "Domingo",
                                "Lunes",
                                "Martes",
                                "Miércoles",
                                "Jueves",
                                "Viernes",
                                "Sábado",
                              ],
                            },
                            months: {
                              shorthand: [
                                "Ene",
                                "Feb",
                                "Mar",
                                "Abr",
                                "May",
                                "Jun",
                                "Jul",
                                "Ago",
                                "Sep",
                                "Oct",
                                "Nov",
                                "Dic",
                              ],
                              longhand: [
                                "Enero",
                                "Febrero",
                                "Marzo",
                                "Abril",
                                "Mayo",
                                "Junio",
                                "Julio",
                                "Agosto",
                                "Septiembre",
                                "Octubre",
                                "Noviembre",
                                "Diciembre",
                              ],
                            },
                          },
                        }}
                        onChange={handleFechasChange}
                        placeholder={`${formatearFechaParaMostrar(
                          fechaInicio
                        )} - ${formatearFechaParaMostrar(fechaFin)}`}
                              />
                            </div>
                            <small className="text-muted d-block mt-2">
                              <i className="ri-calendar-check-line me-1"></i>
                              <strong>Seleccione rango de fechas para filtrar</strong>
                            </small>
                            <small className="text-warning d-block mt-1" style={{fontSize: '0.7rem'}}>
                              <i className="ri-information-line me-1"></i>
                              Los widgets de abajo incluyen todas las facturas registradas, sin importar estatus de pago.
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  )}
                  {/* Botones de exportación desactivados temporalmente */}
                  {false && (
                    <div className="col-sm-auto">
                      <div className="btn-group" role="group">
                        <button
                          type="button"
                          className="btn btn-success btn-sm"
                          onClick={() => handleExport("excel")}
                          disabled={exportando}
                          title="Descargar en Excel"
                        >
                          <i className="ri-file-excel-2-line me-1"></i>
                          {exportando ? "Exportando..." : "Excel"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => handleExport("pdf")}
                          disabled={exportando}
                          title="Descargar en PDF"
                        >
                          <i className="ri-file-pdf-line me-1"></i>
                          {exportando ? "Exportando..." : "PDF"}
                        </button>
                      </div>
                      <small className="text-muted d-block mt-1">
                        Exportar dashboard
                      </small>
                    </div>
                  )}
                  {/* <div className="col-sm-auto">
                    <button
                      type="button"
                      className="btn btn-info btn-sm"
                      onClick={() => setModalDocumentacion(true)}
                      title="Ver documentación de métricas"
                    >
                      <i className="ri-book-open-line me-1"></i>
                      Documentación
                    </button>
                    <small className="text-muted d-block mt-1">
                      Guía de cálculos
                    </small>
                  </div> */}
                  {datosDesdeCache && (
                    <div className="col-auto">
                      <span className="badge bg-info text-white">
                        <i className="mdi mdi-database-check me-1"></i>
                        Datos en caché
                      </span>
                    </div>
                  )}
                  {cargandoFarmacia && (
                    <div className="col-auto">
                      <span className="badge bg-warning text-white">
                        <i className="mdi mdi-loading mdi-spin me-1"></i>
                        Cargando farmacia
                      </span>
                    </div>
                  )}
                </Row>
              </form>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Modal de Documentación de Métricas */}
      <ModalDocumentacionMetricas
        isOpen={modalDocumentacion}
        toggle={() => setModalDocumentacion(!modalDocumentacion)}
      />
    </React.Fragment>
  );
};

export default Section;
